import type { Tagged } from 'type-fest';
import type { Account } from '../account';
import { MutableEntity } from '../common';
import { Group } from '../group';
import { Organization } from '../organization';
import { Product } from '../product';
import { UniqueId } from '../UniqueId';

// 200万回生成したときの衝突確率が1%
// こちらのサイトの計算で計算 => https://zelark.github.io/nano-id-cc/
const uuidGenerator = UniqueId.GetFactory(8);
const publicKeyGenerator = UniqueId.GetFactory(32);

export type Project = MutableEntity & {
  id: Project.Id;
  publicKey?: Project.PublicKey;
  organizationId: Organization.Id;
  name: string;
  productSettings: Project.ProductSettings;
  permissionMemberLimits: Project.PermissionMemberLimits;
  permissionMemberCurrentCounts: Project.PermissionMemberCurrentCounts;
  featureFlags: Project.FeatureFlags;
  allowedIpRanges: string[];
  mfaRequired: boolean;
};

export namespace Project {
  export type Id = Tagged<UniqueId, 'projectId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export const FromString = (id: string): Id => uuidGenerator.FromString(id) as Id;
  }
  export type PublicKey = Tagged<UniqueId, 'projectPublicKey'>;
  export namespace PublicKey {
    export const Generate = () => publicKeyGenerator.generate() as PublicKey;
    export const FromString = (publicKey: string): PublicKey => publicKeyGenerator.FromString(publicKey) as PublicKey;
  }

  export type ProductSettings = {
    [P in Product.Key]?: { plan: Product.Plan<P> };
  };
  export namespace ProductSettings {
    export const Default = (): { [P in Product.Key]: { plan: Product.Plan<P> } } => ({
      knowledge: { plan: 'none' },
      desk: { plan: 'none' },
      neuro: { plan: 'none' },
      troppus: { plan: 'none' },
      voc: { plan: 'none' },
      speak: { plan: 'none' },
      debeso: { plan: 'none' },
      smartFaq: { plan: 'none' },
      tabula: { plan: 'none' },
    });
  }

  export type FeatureFlags = {
    pciDssApplicable?: boolean;
    qantSmsEnabled?: boolean;
  };
  export namespace FeatureFlags {
    export const Default = (): FeatureFlags => ({});

    export const ForResponse = (flags: FeatureFlags = Default()): FeatureFlags => ({ ...flags });

    export const ForUpdate = (incoming: FeatureFlags, stored: FeatureFlags = Default()): FeatureFlags => ({
      ...stored,
      ...incoming,
    });
  }

  export type PermissionMemberLimitDefinition = {
    [K in Product.Key]: {
      productKey: K;
      permissionKey: keyof Group.Policy.ProductPolicy<K> & string;
      label: string;
    };
  }[Product.Key];
  export const PermissionMemberLimitDefinitions = {
    // 'neuro.isAvailable': {
    //   productKey: 'neuro',
    //   permissionKey: 'isAvailable',
    //   label: 'AIオペレーター利用者',
    // },
    // 'neuro.isAdmin': {
    //   productKey: 'neuro',
    //   permissionKey: 'isAdmin',
    //   label: 'AIオペレーター管理者',
    // },
  } as const satisfies Record<string, PermissionMemberLimitDefinition>;
  export type PermissionMemberLimitKey = keyof typeof PermissionMemberLimitDefinitions;
  export type PermissionMemberLimits = Record<string, number | undefined>;
  export type PermissionMemberCurrentCounts = Record<string, number>;
  export namespace PermissionMemberLimits {
    const keys = Object.keys(PermissionMemberLimitDefinitions);

    export const Default = (): PermissionMemberLimits => ({});

    export const Construct = (raw?: unknown): PermissionMemberLimits => {
      if (!raw || typeof raw !== 'object') return Default();
      const record = raw as Record<string, unknown>;
      const result: PermissionMemberLimits = {};
      for (const key of keys) {
        const value = record[key];
        if (typeof value !== 'number') continue;
        if (!Number.isInteger(value)) continue;
        if (value < 0) continue;
        result[key] = value;
      }
      return result;
    };

    export const FindExceededKeys = (params: {
      limits: PermissionMemberLimits;
      currentCounts: PermissionMemberCurrentCounts;
      additionalCounts: PermissionMemberCurrentCounts;
    }): string[] => {
      const { limits, currentCounts, additionalCounts } = params;
      const exceededKeys: string[] = [];

      for (const [key, limit] of Object.entries(limits)) {
        if (typeof limit !== 'number') continue;

        const additional = additionalCounts[key] ?? 0;
        if (additional <= 0) continue;

        const current = currentCounts[key] ?? 0;
        if (current + additional > limit) {
          exceededKeys.push(key);
        }
      }

      return exceededKeys;
    };
  }
  export namespace PermissionMemberCurrentCounts {
    const keys = Object.keys(PermissionMemberLimitDefinitions);
    export const Default = (): PermissionMemberCurrentCounts => {
      const counts = {} as PermissionMemberCurrentCounts;
      for (const key of keys) counts[key] = 0;
      return counts;
    };

    const isPolicyMatched = (params: { policy: Group.Policy; definition: PermissionMemberLimitDefinition }): boolean => {
      const { policy, definition } = params;
      const productPolicy = policy[definition.productKey];
      if (!productPolicy) return false;
      return (productPolicy as Record<string, boolean | undefined>)[definition.permissionKey] === true;
    };

    export const FromPolicy = (params: { policy: Group.Policy; multiplier?: number }): PermissionMemberCurrentCounts => {
      const { policy, multiplier = 1 } = params;
      const counts = Default();
      if (multiplier <= 0) return counts;

      const definitions = Object.entries(PermissionMemberLimitDefinitions) as [string, PermissionMemberLimitDefinition][];
      for (const [key, definition] of definitions) {
        if (!isPolicyMatched({ policy, definition })) continue;
        counts[key] = (counts[key] ?? 0) + multiplier;
      }
      return counts;
    };

    export const FromPositiveDiffPolicies = (params: {
      beforePolicy: Group.Policy;
      afterPolicy: Group.Policy;
      multiplier?: number;
    }): PermissionMemberCurrentCounts => {
      const { beforePolicy, afterPolicy, multiplier = 1 } = params;
      const counts = Default();
      if (multiplier <= 0) return counts;

      const definitions = Object.entries(PermissionMemberLimitDefinitions) as [string, PermissionMemberLimitDefinition][];
      for (const [key, definition] of definitions) {
        const beforeMatched = isPolicyMatched({ policy: beforePolicy, definition });
        const afterMatched = isPolicyMatched({ policy: afterPolicy, definition });
        if (!beforeMatched && afterMatched) {
          counts[key] = (counts[key] ?? 0) + multiplier;
        }
      }
      return counts;
    };
  }

  export type Props = MutableEntity.Props & {
    id: string;
    publicKey?: string | null;
    name: string;
    organizationId: string;
    productSettings: ProductSettings;
    permissionMemberLimits?: unknown;
    permissionMemberCurrentCounts?: unknown;
    featureFlags?: FeatureFlags;
    allowedIpRanges: string[];
    mfaRequired?: boolean;
  };

  export const Construct = (props: Props): Project => {
    return {
      ...MutableEntity.Construct(props),
      id: Id.FromString(props.id),
      publicKey: props.publicKey ? PublicKey.FromString(props.publicKey) : undefined,
      name: props.name,
      organizationId: Organization.Id.FromString(props.organizationId),
      productSettings: props.productSettings,
      permissionMemberLimits: PermissionMemberLimits.Construct(props.permissionMemberLimits),
      permissionMemberCurrentCounts:
        (props.permissionMemberCurrentCounts as PermissionMemberCurrentCounts | undefined) ?? PermissionMemberCurrentCounts.Default(),
      featureFlags: props.featureFlags ?? FeatureFlags.Default(),
      allowedIpRanges: props.allowedIpRanges,
      mfaRequired: props.mfaRequired ?? false,
    };
  };

  export namespace Create {
    export type Props = Pick<Project, 'name' | 'organizationId'> & Partial<Pick<Project, 'mfaRequired'>>;
    export type Params = {
      operatorId: Account.Id;
      props: Props;
    };
  }
  export const Create = ({ operatorId, props }: Create.Params): Project => {
    const id = Id.Generate();
    return {
      ...MutableEntity.Create({ operatorId }),
      id: id,
      publicKey: PublicKey.Generate(),
      name: props.name,
      organizationId: props.organizationId,
      productSettings: ProductSettings.Default(),
      permissionMemberLimits: PermissionMemberLimits.Default(),
      permissionMemberCurrentCounts: PermissionMemberCurrentCounts.Default(),
      featureFlags: FeatureFlags.Default(),
      allowedIpRanges: [],
      mfaRequired: props.mfaRequired ?? false,
    };
  };

  export namespace Update {
    export type UpdatableKey = 'name' | 'productSettings' | 'featureFlags' | 'allowedIpRanges' | 'mfaRequired';
    export type UpdateProps = Partial<Pick<Project, UpdatableKey>> & {
      permissionMemberLimits?: PermissionMemberLimits;
    };
    export type Params = {
      operatorId: Account.Id;
      cur: Project;
      updateProps: UpdateProps;
    };
  }
  export const Update = ({ operatorId, cur, updateProps }: Update.Params): Project => {
    const { permissionMemberLimits: permissionMemberLimitsUpdate, featureFlags: featureFlagsUpdate, ...rest } = updateProps;
    const permissionMemberLimits =
      permissionMemberLimitsUpdate !== undefined
        ? PermissionMemberLimits.Construct(permissionMemberLimitsUpdate)
        : cur.permissionMemberLimits;
    const featureFlags = featureFlagsUpdate !== undefined ? FeatureFlags.ForUpdate(featureFlagsUpdate, cur.featureFlags) : cur.featureFlags;
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId }),
      ...rest,
      permissionMemberLimits,
      featureFlags,
    };
  };

  export const isProductEnabled = (params: { productSettings: Project['productSettings']; productKey: Product.Key }): boolean => {
    const { productSettings, productKey } = params;
    const settings = productSettings[productKey];
    return !!settings && settings.plan !== 'none';
  };

  /** PCI-DSS対象プロジェクトで、MFAを必須にする利用中の製品を返す。 */
  export const getMfaRequiredProducts = (project: Pick<Project, 'featureFlags' | 'productSettings'>): Product[] =>
    project.featureFlags.pciDssApplicable === true
      ? [Product.Speak, Product.Neuro].filter((product) =>
          isProductEnabled({ productSettings: project.productSettings, productKey: product.key }),
        )
      : [];

  /** PCI-DSS対象でSpeakまたはAIオペレーターを利用中の場合にMFAを必須と判定する。 */
  export const isMfaRequiredByProduct = (project: Pick<Project, 'featureFlags' | 'productSettings'>): boolean =>
    getMfaRequiredProducts(project).length > 0;

  // TODO: 移行期間中のため、権限グループによるアクセス制御は一部プロダクトのみ対象。最終的には全プロダクトに適用予定
  const POLICY_GATED_PRODUCTS: ReadonlySet<Product.Key> = new Set<Product.Key>([
    'knowledge',
    'troppus',
    'neuro',
    'voc',
    'speak',
    'debeso',
    'smartFaq',
    'tabula',
  ]);

  // プロダクト利用設定が有効、かつ権限グループで利用権限以上があるかを判定
  export const isProductAccessible = (params: {
    productSettings: Project['productSettings'];
    productKey: Product.Key;
    group?: Pick<Group, 'policy'> | null | undefined;
    account?: { isEmployee?: boolean } | null | undefined;
  }): boolean => {
    const { productSettings, productKey, group, account } = params;
    if (!Project.isProductEnabled({ productSettings, productKey })) return false;
    if (POLICY_GATED_PRODUCTS.has(productKey)) {
      if (productKey === 'voc' && account?.isEmployee) return true;
      if (!group) return false;
      if (!Group.Policy.IsAvailable({ policy: group.policy, productKey })) return false;
    }
    return true;
  };

  export const defaultProduct = (params: {
    project: Pick<Project, 'productSettings'>;
    group?: Pick<Group, 'policy'> | null | undefined;
    account?: { isEmployee?: boolean } | null | undefined;
  }): Product | 'no_products_in_use' => {
    const { project, group, account } = params;
    const product = Product.List.find((product) =>
      Project.isProductAccessible({ productSettings: project.productSettings, productKey: product.key, group, account }),
    );
    return product || 'no_products_in_use';
  };
}
